import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useAxesStore } from '../store/useAxesStore';
import { Loader2, LayoutGrid, Clock, Trash2 } from 'lucide-react';

interface SavedPlan {
    id: string;
    plan_name: string;
    updated_at: string;
    plan_data: any;
}

const MyPlansGallery: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { loadPlan } = useAxesStore();
    const [plans, setPlans] = useState<SavedPlan[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchPlans = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('user_saved_resplans')
                .select('id, plan_name, updated_at, plan_data')
                .eq('user_id', user.id)
                .order('updated_at', { ascending: false });

            if (error) throw error;
            if (data) setPlans(data);
        } catch (err) {
            console.error('Error fetching plans:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, [user]);

    const handleLoadPlan = async (planId: string) => {
        await loadPlan(planId);
        navigate('/app/axes');
    };

    const handleDeletePlan = async (e: React.MouseEvent, planId: string) => {
        e.stopPropagation();
        if (!confirm('Are you sure you want to delete this plan?')) return;

        try {
            const { error } = await supabase
                .from('user_saved_resplans')
                .delete()
                .eq('id', planId)
                .eq('user_id', user?.id || ''); // Extra safety

            if (error) throw error;
            setPlans(plans.filter(p => p.id !== planId));
        } catch (err) {
            console.error('Error deleting plan:', err);
            alert('Failed to delete plan.');
        }
    };

    return (
        <DashboardLayout>
            <div className="flex flex-col h-screen bg-stone-950 text-zinc-100 font-sans">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-stone-800 bg-stone-950/50 backdrop-blur-md sticky top-0 z-30">
                    <div className="flex items-center gap-4">
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-stone-500 bg-clip-text text-transparent">
                            My Saved Plans
                        </h1>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 relative">
                    {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 size={48} className="animate-spin text-indigo-500" />
                        </div>
                    ) : plans.length === 0 ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-600">
                            <p className="text-xl font-medium mb-2">No plans saved yet</p>
                            <p className="text-sm">Start designing in the Axes Explorer to save your work</p>
                            <button
                                onClick={() => navigate('/app/axes')}
                                className="mt-6 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                            >
                                Go to Axes Explorer
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                            {plans.map((plan) => {
                                const areaRounded = plan.plan_data?.total_area_m2
                                    ? Math.round(plan.plan_data.total_area_m2)
                                    : '?';
                                const roomsCount = plan.plan_data?.nodes?.length || 0;

                                return (
                                    <div
                                        key={plan.id}
                                        onClick={() => handleLoadPlan(plan.id)}
                                        className="group relative bg-stone-900 border border-stone-800 rounded-xl overflow-hidden cursor-pointer hover:border-indigo-500/50 hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-200 flex flex-col"
                                    >
                                        <div className="aspect-video bg-stone-800 flex items-center justify-center relative border-b border-stone-800/50">
                                            {/* We use a placeholder icon instead of a thumbnail for now */}
                                            <LayoutGrid className="text-stone-700" size={48} strokeWidth={1} />
                                            <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-transparent to-transparent opacity-60"></div>
                                        </div>

                                        <div className="p-4 flex-1 flex flex-col">
                                            <h3 className="font-semibold text-lg text-white mb-2 truncate">
                                                {plan.plan_name}
                                            </h3>

                                            <div className="flex flex-col gap-1.5 mt-auto shrink-0 text-xs text-stone-400">
                                                <div className="flex items-center gap-2">
                                                    <LayoutGrid size={14} />
                                                    <span>{roomsCount} Rooms • {areaRounded}m²</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Clock size={14} />
                                                    <span>{new Date(plan.updated_at).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Actions overlay */}
                                        <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => handleDeletePlan(e, plan.id)}
                                                className="p-2 bg-black/50 hover:bg-red-500/90 text-white/70 hover:text-white rounded-lg backdrop-blur shadow-sm transition-all"
                                                title="Delete plan"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default MyPlansGallery;
